import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Clear as ClearIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  FileDownloadOutlined as ExportIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { toast } from "react-hot-toast";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { useAuth } from "../../contexts/auth";
import { baseAxios, Context } from "../../utils/config";
import { SCHEDULER_TIMEZONE } from "../Scheduler/config";
import {
  ROLE_JDTECH_ADMIN,
  ROLE_SALON_ADMIN,
  ROLE_SYSTEM_ADMIN,
} from "../../utils/constants";
import { colors } from "../../theme/colors";

dayjs.extend(utc);
dayjs.extend(timezone);

const ROWS_PER_PAGE_OPTIONS = [15, 30, 50, 100];

const MARK_STYLE = {
  backgroundColor: "rgba(197, 160, 111, 0.45)",
  color: "inherit",
  borderRadius: 4,
};

const COLUMNS = [
  { id: "expand", label: "", sortable: false, minWidth: 52 },
  {
    id: "client_name",
    label: "Client",
    field: "client_name",
    minWidth: 160,
  },
  { id: "date", label: "Date", field: "date", minWidth: 130 },
  {
    id: "transaction_count",
    label: "Transactions",
    field: "transaction_count",
    minWidth: 120,
  },
  {
    id: "total_waste",
    label: "Total Waste",
    field: "total_waste",
    minWidth: 130,
  },
];

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .filter((t) => t.length > 1)
    .map(escapeRegex);

  const phrase = q.includes(" ") ? escapeRegex(q) : null;
  const patterns = [phrase, ...tokens].filter(Boolean);
  if (!patterns.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${patterns.join("|")})`, "i");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

function toUtcDayjs(value) {
  if (!value) return null;
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return dayjs.utc(`${str}T12:00:00.000Z`);
  }
  const parsed = dayjs.utc(str);
  return parsed.isValid() ? parsed : null;
}

function formatDate(value) {
  const parsed = toUtcDayjs(value);
  return parsed ? parsed.tz(SCHEDULER_TIMEZONE).format("YYYY-MM-DD") : "";
}

function formatTime(value) {
  if (!value) return "";
  const parsed = dayjs.utc(String(value).trim());
  return parsed.isValid() ? parsed.tz(SCHEDULER_TIMEZONE).format("HH:mm:ss") : "";
}

function formatGroupDate(row) {
  const sample = row.transactions?.[0]?.start_time || row.date;
  return formatDate(sample);
}

function formatNumber(value) {
  const number = Number(value);
  if (Number.isNaN(number)) return value ?? "";
  return number.toFixed(2);
}

function gramsLabel(value) {
  const formatted = formatNumber(value);
  return formatted ? `${formatted} g` : "-";
}

function getClientName(row) {
  const fromParts = `${row.client_first_name || ""} ${row.client_last_name || ""}`
    .replace(/\s+/g, " ")
    .trim();
  return fromParts || String(row.transaction_client_name || "").trim();
}

function getStylistName(transaction) {
  return `${transaction.stylist_first_name || ""} ${transaction.stylist_last_name || ""}`
    .replace(/\s+/g, " ")
    .trim();
}

function getGroupKey(row) {
  return `${row.client_id ?? "null"}-${row.date}`;
}

export default function ReWeightReport() {
  const { user } = useAuth();
  const { location: globalLocation } = useContext(Context);

  const isAdmin = user?.userRoles?.some((role) =>
    [ROLE_SALON_ADMIN, ROLE_SYSTEM_ADMIN, ROLE_JDTECH_ADMIN].includes(role),
  );
  const stylistId = user?.userID || user?.id;

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [hideNonWaste, setHideNonWaste] = useState(false);
  const [expanded, setExpanded] = useState({});

  const selectedLocationId = globalLocation?.location_id;
  const selectedLocationName =
    selectedLocationId && selectedLocationId !== -1
      ? globalLocation?.location_name
      : "";

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchReweightData = useCallback(
    (nextPage, nextLimit, orderBy = sortBy, order = sortOrder) => {
      setLoading(true);
      setPage(nextPage);
      setRowsPerPage(nextLimit);
      const params = {
        page: nextPage,
        limit: nextLimit,
        sortBy: orderBy,
        sortOrder: order,
      };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (!isAdmin && stylistId) params.stylistId = stylistId;
      params.hideNonWaste = hideNonWaste ? "true" : "false";

      baseAxios
        .get("/transactions/reports/re-weight", { params })
        .then((res) => {
          setRows(res?.data?.Items ?? []);
          setTotalCount(res?.data?.totalCount ?? 0);
          setExpanded({});
        })
        .catch(() => {
          setRows([]);
          setTotalCount(0);
          toast.error("Error loading re-weight transactions", { duration: 3000 });
        })
        .finally(() => setLoading(false));
    },
    [sortBy, sortOrder, debouncedSearch, isAdmin, stylistId, hideNonWaste],
  );

  useEffect(() => {
    fetchReweightData(0, rowsPerPage);
  }, [debouncedSearch, selectedLocationId, hideNonWaste]);

  const handleRefresh = useCallback(() => {
    fetchReweightData(page, rowsPerPage);
  }, [fetchReweightData, page, rowsPerPage]);

  const handleExport = useCallback(async () => {
    if (!rows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }
    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Re-Weight Report");
      worksheet.columns = [
        { header: "Client", key: "client_name", width: 22 },
        { header: "Date", key: "date", width: 14 },
        { header: "Transactions", key: "transaction_count", width: 14 },
        { header: "Total Waste (g)", key: "total_waste", width: 16 },
        { header: "Stylist", key: "stylist", width: 20 },
        { header: "Salon", key: "salon", width: 20 },
        { header: "Start Time", key: "start_time", width: 12 },
        { header: "Waste (g)", key: "waste", width: 12 },
      ];
      worksheet.getRow(1).font = { bold: true };
      rows.forEach((row) => {
        const clientName = getClientName(row);
        const date = formatGroupDate(row);
        const children = row.transactions ?? [];
        if (!children.length) {
          worksheet.addRow({
            client_name: clientName,
            date,
            transaction_count: row.transaction_count ?? 0,
            total_waste: Number(row.total_waste) || 0,
          });
          return;
        }
        children.forEach((tx) => {
          worksheet.addRow({
            client_name: clientName,
            date,
            transaction_count: row.transaction_count ?? 0,
            total_waste: Number(row.total_waste) || 0,
            stylist: getStylistName(tx),
            salon: tx.salon_name || "",
            start_time: formatTime(tx.start_time),
            waste: Number(tx.waste) || 0,
          });
        });
      });
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `ReWeightReport_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [rows]);

  const handlePageChange = (_, value) => {
    fetchReweightData(value - 1, rowsPerPage);
  };

  const handleRowsPerPageChange = (e) => {
    fetchReweightData(0, Number(e.target.value));
  };

  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(field);
    setSortOrder(newOrder);
    fetchReweightData(0, rowsPerPage, field, newOrder);
  };

  const toggleExpanded = (groupKey) => {
    setExpanded((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const from = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(page * rowsPerPage + rowsPerPage, totalCount);
  const tableMinWidth = COLUMNS.reduce(
    (sum, col) => sum + (col.minWidth || 0),
    0,
  );

  const renderWaste = (value) => {
    const waste = Number(value);
    const hasWaste = !Number.isNaN(waste) && waste > 0;
    return (
      <Chip
        size="small"
        variant={hasWaste ? "filled" : "outlined"}
        label={
          <HighlightText text={gramsLabel(value)} query={debouncedSearch} />
        }
        sx={{
          height: 24,
          fontWeight: 600,
          bgcolor: hasWaste ? colors.gold : "transparent",
          color: hasWaste ? colors.black : "text.secondary",
          borderColor: hasWaste ? colors.gold : "divider",
          "&:hover": {
            bgcolor: hasWaste
              ? colors.gold
              : (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "transparent",
          },
          "& .MuiChip-label": { px: 1 },
        }}
      />
    );
  };

  const renderSalon = (transaction) => (
    <Box sx={{ py: 0.25 }}>
      <Typography fontWeight={500} fontSize={14} component="div">
        <HighlightText
          text={transaction.salon_name || "-"}
          query={debouncedSearch}
        />
      </Typography>
      {transaction.organization_name ? (
        <Typography
          fontSize={12}
          color="text.secondary"
          component="div"
          sx={{ display: "block", mt: 0.25, lineHeight: 1.3 }}
        >
          <HighlightText
            text={transaction.organization_name}
            query={debouncedSearch}
          />
        </Typography>
      ) : null}
    </Box>
  );

  const renderCell = (row, col) => {
    if (col.id === "expand") {
      const children = row.transactions ?? [];
      const expandedKey = getGroupKey(row);
      return (
        <IconButton
          size="small"
          aria-label={expanded[expandedKey] ? "Collapse row" : "Expand row"}
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded(expandedKey);
          }}
          disabled={!children.length}
        >
          {expanded[expandedKey] ? <CollapseIcon /> : <ExpandIcon />}
        </IconButton>
      );
    }
    if (col.id === "date") {
      return (
        <Typography component="span" fontWeight={500} fontSize={14}>
          <HighlightText
            text={formatGroupDate(row) || "-"}
            query={debouncedSearch}
          />
        </Typography>
      );
    }
    if (col.id === "total_waste") return renderWaste(row.total_waste);
    if (col.id === "client_name") {
      const clientName = getClientName(row);
      return clientName ? (
        <HighlightText text={clientName} query={debouncedSearch} />
      ) : (
        "-"
      );
    }
    if (col.id === "transaction_count") {
      return (
        <HighlightText
          text={String(row.transaction_count ?? 0)}
          query={debouncedSearch}
        />
      );
    }

    const value = row[col.field];
    return value ? (
      <HighlightText text={String(value)} query={debouncedSearch} />
    ) : (
      "-"
    );
  };

  const renderChildRows = (row) => {
    const children = row.transactions ?? [];
    const expandedKey = getGroupKey(row);
    if (!expanded[expandedKey] || !children.length) return null;

    return (
      <TableRow>
        <TableCell colSpan={COLUMNS.length} sx={{ p: 0, borderBottom: 0 }}>
          <Collapse in timeout="auto" unmountOnExit>
            <Box
              sx={{
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "#141416" : "#f2f2f2",
                borderTop: "1px solid",
                borderBottom: "1px solid",
                borderColor: "divider",
                px: { xs: 2, md: 7 },
                py: 2,
              }}
            >
              <Table
                size="small"
                aria-label="re-weight transaction details"
                sx={{
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "6px",
                  overflow: "hidden",
                  "& th": {
                    bgcolor: "secondary.main",
                    color: "common.white",
                    fontSize: 12,
                    fontWeight: 700,
                    py: 0.75,
                    borderBottom: "1px solid",
                    borderColor: "primary.main",
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                  },
                  "& td": {
                    py: 1,
                    borderColor: "divider",
                    verticalAlign: "top",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Stylist</TableCell>
                    <TableCell>Salon</TableCell>
                    <TableCell width={130}>Start Time</TableCell>
                    <TableCell width={120}>Waste</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {children.map((transaction, index) => (
                    <TableRow
                      key={transaction.transaction_id ?? `child-${index}`}
                      sx={{
                        "&:last-child td": { borderBottom: 0 },
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? index % 2 === 0
                              ? "#1b1b1e"
                              : "#141416"
                            : index % 2 === 0
                              ? "white"
                              : "#fcfcfd",
                      }}
                    >
                      <TableCell>
                        <Typography fontSize={13} fontWeight={500}>
                          <HighlightText
                            text={getStylistName(transaction) || "-"}
                            query={debouncedSearch}
                          />
                        </Typography>
                      </TableCell>
                      <TableCell>{renderSalon(transaction)}</TableCell>
                      <TableCell>
                        <Typography fontSize={13}>
                          <HighlightText
                            text={formatTime(transaction.start_time) || "-"}
                            query={debouncedSearch}
                          />
                        </Typography>
                      </TableCell>
                      <TableCell>{renderWaste(transaction.waste)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mt: 1.5,
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Re-Weight Transactions Report
            </Typography>
            {selectedLocationName ? (
              <Chip
                size="small"
                label={selectedLocationName}
                sx={{
                  bgcolor: colors.black,
                  color: colors.gold,
                  fontWeight: 600,
                  borderRadius: "6px",
                }}
              />
            ) : null}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Client re-weigh totals with expandable transaction details.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Tooltip title="Show only transactions with waste recorded">
            <Box
              component="label"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                height: 40,
                pl: 0.5,
                pr: 1.25,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "6px",
                cursor: "pointer",
                "&:hover": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "grey.50",
                },
              }}
            >
              <Switch
                checked={hideNonWaste}
                onChange={(e) => setHideNonWaste(e.target.checked)}
                size="small"
                inputProps={{ "aria-label": "Show only transactions with waste" }}
                sx={{
                  m: 0,
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: colors.gold,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: colors.gold,
                  },
                }}
              />
              <Typography
                component="span"
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color: "text.secondary",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                Has waste
              </Typography>
            </Box>
          </Tooltip>
          <IconButton
            aria-label="Refresh re-weight transactions"
            onClick={handleRefresh}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: "6px",
              "&:hover": {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "grey.100",
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={loading || !rows.length}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
            }}
          >
            Export
          </Button>
          <TextField
            size="small"
            placeholder="Search client, stylist, salon, waste..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 340,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon sx={{ fontSize: 20, color: "grey.400" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": {
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "grey.100" : "grey.800",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "grey.200",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Box>

      <TableContainer
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "6px",
          overflowX: "auto",
          overflowY: "hidden",
          bgcolor: "background.paper",
        }}
      >
        <Table
          aria-label="re-weight transactions table"
          sx={{ width: "100%", minWidth: tableMinWidth }}
        >
          <TableHead
            sx={{
              bgcolor: "secondary.main",
              borderBottom: "1px solid",
              borderColor: "primary.main",
              "& .MuiTableCell-head": {
                color: "common.white",
                fontWeight: 600,
                bgcolor: "secondary.main",
              },
              "& .MuiTableSortLabel-root": {
                color: "common.white",
                "&.Mui-active": { color: colors.gold },
                "&:hover": { color: colors.gold },
                "& .MuiTableSortLabel-icon": {
                  color: `${colors.gold} !important`,
                },
              },
            }}
          >
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  sx={{
                    minWidth: col.minWidth,
                    fontWeight: 600,
                    padding: "8px 8px 8px 16px",
                  }}
                >
                  {col.sortable === false || !col.field ? (
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight={600}
                    >
                      {col.label}
                    </Typography>
                  ) : (
                    <TableSortLabel
                      active={sortBy === col.field}
                      direction={sortBy === col.field ? sortOrder : "asc"}
                      onClick={() => handleSort(col.field)}
                    >
                      {col.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: rowsPerPage }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.id} sx={{ p: "6px 4px 6px 16px" }}>
                      <Skeleton variant="text" sx={{ height: 40 }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length}
                  align="center"
                  sx={{ py: 8 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No data to display
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, i) => (
                <React.Fragment key={getGroupKey(row) ?? `row-${i}`}>
                  <TableRow
                    onClick={() => {
                      if ((row.transactions ?? []).length) {
                        toggleExpanded(getGroupKey(row));
                      }
                    }}
                    sx={{
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "#141416" : "background.paper",
                      cursor: (row.transactions ?? []).length
                        ? "pointer"
                        : "default",
                      "&:nth-of-type(4n-3)": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "#1b1b1e" : "#fafafa",
                      },
                      "&:hover": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "#2a2a2e" : "#f5f5f5",
                      },
                      "& td": { color: "text.primary", borderColor: "divider" },
                    }}
                  >
                    {COLUMNS.map((col) => (
                      <TableCell
                        key={col.id}
                        sx={{
                          minWidth: col.minWidth,
                          padding: "6px 4px 6px 16px",
                        }}
                      >
                        {renderCell(row, col)}
                      </TableCell>
                    ))}
                  </TableRow>
                  {renderChildRows(row)}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && totalCount > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            px: 2,
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Items per page:
            </Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              sx={{ minWidth: 64, fontSize: "14px", height: "28px" }}
            >
              {ROWS_PER_PAGE_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="body2" color="text.secondary">
              {from}-{to} of {totalCount} groups
            </Typography>
          </Box>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handlePageChange}
            showFirstButton
            showLastButton
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
}
